// Servicio para manejar productos desde la API local
class ProductosService {
  constructor() {
    // Usar variable de entorno o detectar automáticamente el entorno
    this.baseURL =
      process.env.REACT_APP_API_URL ||
      (window.location.origin.includes('localhost')
        ? 'http://localhost:8080/api'
        : `${window.location.origin}/api`);
  }

  /**
   * Obtiene todos los productos con filtros opcionales
   * @param {Object} params - Parámetros de consulta
   * @param {string} params.tipo - Filtrar por tipo
   * @param {string} params.oferta - Filtrar por oferta ('Si'/'No')
   * @param {boolean} params.stock - Solo productos con stock
   * @param {number} params.page - Página para paginación
   * @param {number} params.limit - Límite de productos por página
   * @param {string} params.order - Campo para ordenar
   * @param {string} params.direction - Dirección de orden ('asc'/'desc')
   * @returns {Promise<Object>} Respuesta con productos y paginación
   */
  async getProductos(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      // Agregar parámetros si están definidos
      if (params.tipo && params.tipo.length > 0) queryParams.append('tipo', params.tipo);
      if (params.oferta) queryParams.append('oferta', params.oferta);
      if (params.stock) queryParams.append('stock', 'true');
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.order) queryParams.append('order', params.order);
      if (params.direction) queryParams.append('direction', params.direction);
      if (params.search) queryParams.append('search', params.search);

      const url = `${this.baseURL}/productos?${queryParams.toString()}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data = await response.json();

      // Normalizar datos para compatibilidad con componentes existentes
      return {
        productos: data.productos.map(this.normalizeProduct),
        pagination: data.pagination,
        total: data.pagination.total,
      };
    } catch (error) {
      console.error('Error obteniendo productos:', error);
      throw error;
    }
  }

  /**
   * Obtiene un producto específico por ID
   * @param {number} id - ID del producto
   * @returns {Promise<Object>} Producto normalizado
   */
  async getProducto(id) {
    try {
      const response = await fetch(`${this.baseURL}/productos/${id}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Producto no encontrado');
        }
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const producto = await response.json();
      return this.normalizeProduct(producto);
    } catch (error) {
      console.error('Error obteniendo producto:', error);
      throw error;
    }
  }

  /**
   * Obtiene el producto marcado como destacado para el banner de la landing.
   * A diferencia de getProducto, la ausencia de destacado es un estado
   * normal (nadie lo ha marcado, o se desmarcó) y no un error: se resuelve
   * a null en vez de lanzar, para que el banner simplemente no se muestre.
   * @returns {Promise<Object|null>} Producto normalizado, o null si no hay ninguno
   */
  async getProductoDestacado() {
    try {
      const response = await fetch(`${this.baseURL}/productos/destacado`);

      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const producto = await response.json();
      return this.normalizeProduct(producto);
    } catch (error) {
      console.error('Error obteniendo el producto destacado:', error);
      throw error;
    }
  }

  /**
   * Obtiene productos agrupados por tipo
   * @returns {Promise<Array>} Array con tipos y cantidades
   */
  async getProductosPorTipo() {
    try {
      const response = await fetch(`${this.baseURL}/productos/tipos`);

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error obteniendo tipos de productos:', error);
      throw error;
    }
  }

  /**
   * Obtiene estadísticas generales de productos
   * @returns {Promise<Object>} Estadísticas del catálogo
   */
  async getEstadisticas() {
    try {
      const response = await fetch(`${this.baseURL}/productos/estadisticas`);

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error obteniendo estadísticas:', error);
      throw error;
    }
  }

  /**
   * Normaliza un producto de la API para compatibilidad con componentes
   * @param {Object} producto - Producto de la API
   * @returns {Object} Producto normalizado
   */
  normalizeProduct(producto) {
    const toNumber = v => {
      if (v === null || v === undefined || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const precioPublico = toNumber(producto.precio_publico);
    const precioOferta = toNumber(producto.precio_oferta);
    const cantidad = toNumber(producto.cantidad);

    return {
      id: producto.id,
      // slug es el segmento legible de la URL de la ficha. Puede venir null en
      // un producto que el ETL acaba de insertar y al que EnsureSlugs (backend)
      // todavía no ha llegado; por eso todos los enlaces caen al id.
      slug: producto.slug || null,
      Juego: producto.juego,
      juego: producto.juego, // Para compatibilidad
      nombre: producto.juego, // Para compatibilidad
      Tipo: producto.tipo,
      tipo: producto.tipo,
      Precio_Publico: precioPublico,
      precio_publico: precioPublico,
      precio: precioPublico, // Para compatibilidad
      Precio_Oferta: precioOferta,
      precio_oferta: precioOferta,
      Oferta: producto.oferta,
      oferta: producto.oferta,
      Preventa: producto.preventa,
      preventa: producto.preventa,
      EsNuevo: producto.es_nuevo,
      es_nuevo: producto.es_nuevo,
      Cantidad: cantidad,
      cantidad: cantidad,
      stock: cantidad, // Para compatibilidad
      Imagen: producto.imagen,
      imagen: producto.imagen,
      descripcion: producto.descripcion,
      marca: producto.marca,
      jugadores: producto.jugadores,
      edad_minima: producto.edad_minima,
      creado_en: producto.creado_en,
      actualizado_en: producto.actualizado_en,

      // Datos de la ficha de producto. Solo vienen de /productos/:id; en el
      // listado quedan undefined y los componentes que los pintan los omiten.
      //
      // Se aplanan a este nivel para que FichaTecnica lea un objeto plano.
      // Antes había aquí isbn, ean, minutos_juego, complejidad y
      // dependencia_idioma: eran columnas de la tabla legacy `productos`, no
      // existen en `catalogo` y siempre llegaban undefined.
      ...(producto.ficha || {}),
      imagenes: producto.imagenes || [],
    };
  }

  /**
   * Obtiene productos en oferta
   * @returns {Promise<Object>} Productos en oferta
   */
  async getProductosEnOferta() {
    return this.getProductos({ oferta: 'Si' });
  }

  /**
   * Obtiene productos con stock disponible
   * @returns {Promise<Object>} Productos con stock
   */
  async getProductosConStock() {
    return this.getProductos({ stock: true });
  }

  /**
   * Busca productos por nombre
   * @param {string} termino - Término de búsqueda
   * @returns {Promise<Array>} Productos que coinciden con la búsqueda
   */
  async buscarProductos(termino) {
    try {
      // Obtener todos los productos y filtrar localmente
      // En el futuro se podría implementar búsqueda en el backend
      const { productos } = await this.getProductos({ limit: 1000 });

      const terminoLower = termino.toLowerCase();
      return productos.filter(
        producto =>
          producto.juego.toLowerCase().includes(terminoLower) ||
          (producto.tipo && producto.tipo.toLowerCase().includes(terminoLower))
      );
    } catch (error) {
      console.error('Error buscando productos:', error);
      throw error;
    }
  }
}

// Crear instancia singleton
const productosService = new ProductosService();

export default productosService;
